import {
  Client,
  PageCollection,
  BatchRequestContent,
  BatchRequestStep,
  BatchResponseContent,
  LargeFileUploadTask,
  FileUpload,
} from '@microsoft/microsoft-graph-client';
import {
  DEFAULT_PAGE_SIZE,
  StorageAdapter,
  StorageCapabilities,
  FeedItemUrlMap,
  StorageItemId,
} from '../common/adapter';
import { StorageManager } from '../common/manager';
import { StorageGetNextListPage, StorageListPage } from '../common/list-page';
import { OneDriveListPage } from './one-drive.list-page';
import {
  BasicStorageItem,
  chunkArray,
  Credential,
  Logger,
  MethodNotImplementedError,
  ServiceAuthType,
  ServiceProvider,
  StorageFolder,
  StorageItem,
} from '@uploadcam/common';
import { OneDriveFile, OneDriveItem } from './one-drive.item';
import { MockItem } from '../common/mock.item';
import { Site } from '@microsoft/microsoft-graph-types';
import { MockListPage } from '../common/mock.list-page';
import { FriendlyError } from '../../../../app/core/utils';

export const ONE_DRIVE_ROOT = 'uploadcam://one-drive';
export const ONE_DRIVE_PERSONAL_DRIVE_ID = 'uploadcam://one-drive/personal-drive';
export const ONE_DRIVE_SHAREPOINT_ID = 'uploadcam://one-drive/sharepoint';
export const ONE_DRIVE_SHARED_WITH_ME_ID = 'uploadcam://one-drive/shared-with-me';

export class OneDriveAdapter implements StorageAdapter {
  private client: Client;
  private isMicrosoftPersonalFull: boolean;
  private manager!: StorageGetNextListPage;

  constructor(
    private readonly credential: Credential,
    private readonly logger: Logger,
    private readonly teamRoot?: StorageFolder,
  ) {
    this.client = this.createClient(credential.accessToken);
    this.isMicrosoftPersonalFull =
      this.credential.authType === ServiceAuthType.MicrosoftPersonalFull;
  }

  private createClient(accessToken: string): Client {
    return Client.init({
      authProvider: (done) => done(undefined, accessToken),
    });
  }

  private async batchRequest<T extends BasicStorageItem, U>(
    items: T[],
    requestFn: (item: T) => Request,
    resultFn: (res: Response) => Promise<U>,
  ): Promise<Record<StorageItemId, U>> {
    // Split items into batches by OneDrive's max batch size
    const batches = Array.from(chunkArray(items, 20));
    const result: Record<StorageItemId, U> = {};

    for (const batch of batches) {
      const batchRequestContent = new BatchRequestContent(
        batch.map<BatchRequestStep>((item) => ({
          id: item.id,
          request: requestFn(item),
        })),
      );

      const content = await batchRequestContent.getContent();
      const batchResponse = await this.client.api('/$batch').post(content);
      const batchResponseContent = new BatchResponseContent(batchResponse);

      const chunkResults = await Promise.all(
        batch.map(async ({ id }) => {
          const res = batchResponseContent.getResponseById(id);
          return [id, await resultFn(res)];
        }),
      );

      Object.assign(result, Object.fromEntries(chunkResults));
    }

    return result;
  }

  public setManager(manager: StorageManager): void {
    this.manager = manager;
  }

  public getCapabilities(): StorageCapabilities {
    return {
      previewThumbnails: true,
      userGroups: false,
    };
  }

  public getProvider(): ServiceProvider {
    return ServiceProvider.Microsoft;
  }

  public async getRootFolder(): Promise<StorageItem> {
    // Root folder points to a different folder based on the OAuth scopes, see `listFolder`
    return MockItem.rootFolder(
      ONE_DRIVE_ROOT,
      ServiceProvider.Microsoft,
      this.teamRoot?.driveId,
      false,
    );
  }

  public async getDownloadUrls(items: BasicStorageItem[]): Promise<FeedItemUrlMap> {
    if (!items || items.length === 0) {
      return {};
    }

    return this.batchRequest(
      items,
      ({ id, driveId }) =>
        new Request(`/drives/${driveId}/items/${id}/`, {
          method: 'GET',
        }),
      async (res) => {
        if (res.ok) {
          const body = await res.json();
          return body['@microsoft.graph.downloadUrl'];
        } else {
          return undefined;
        }
      },
    );
  }

  private getItemPath(item: StorageItem): string {
    return item.driveId ? `/drives/${item.driveId}/items/${item.id}` : `/me/drive/items/${item.id}`;
  }

  public async getFileUrl(file: StorageItem): Promise<string> {
    // TODO: Improve file URL generation for businesss accounts, personal
    // accounts link to the web-app but business ones don't. They just show the
    // file, and the user can't navigate to the parent folder, etc.
    const res = await this.client.api(this.getItemPath(file)).get();
    return res.webUrl;
  }

  public async getFolderUrl(folder: StorageItem): Promise<string> {
    const res = await this.client.api(this.getItemPath(folder)).get();
    return res.webUrl;
  }

  public getProviderUrl(): string {
    return `https://onedrive.live.com`;
  }

  private async getImageUrls(items: BasicStorageItem[], sizeKey: string): Promise<FeedItemUrlMap> {
    if (!items || items.length === 0) {
      return {};
    }

    return this.batchRequest(
      items,
      ({ id, driveId }) =>
        new Request(`/drives/${driveId}/items/${id}/thumbnails?select=${sizeKey}`, {
          method: 'GET',
        }),
      async (res) => {
        if (res.ok) {
          return (await res.json()).value[0][sizeKey]?.url;
        } else {
          return undefined;
        }
      },
    );
  }

  public async getThumbnailUrls(items: BasicStorageItem[]): Promise<FeedItemUrlMap> {
    return this.getImageUrls(items, 'c750x420');
  }

  public async getPreviewUrls(items: BasicStorageItem[]): Promise<FeedItemUrlMap> {
    return this.getImageUrls(items, 'c2000x1120');
  }

  public updateAccessToken(token: string): void {
    this.client = this.createClient(token);
  }

  public async write(parent: StorageItem, file: File): Promise<OneDriveItem> {
    if (parent.id === ONE_DRIVE_ROOT) {
      throw new MethodNotImplementedError('Cannot write to root folder');
    }

    const requestUrl = `https://graph.microsoft.com/v1.0${this.getItemPath(parent)}:/${file.name}:/createUploadSession`;

    const uploadSession = await LargeFileUploadTask.createUploadSession(this.client, requestUrl, {
      '@microsoft.graph.conflictBehavior': 'rename',
      fileSize: file.size,
      name: file.name,
    });

    const task = new LargeFileUploadTask(
      this.client,
      new FileUpload(file, file.name, file.size),
      uploadSession,
    );

    const res = await task.upload();

    return new OneDriveItem(res.responseBody as OneDriveFile, parent.driveId);
  }

  private getEndpoint(folder: StorageItem): string {
    const folderId = folder.id;
    const driveId = folder.driveId;

    if (folderId === ONE_DRIVE_PERSONAL_DRIVE_ID) {
      // Personal root folder
      return '/me/drive/root/children';
    } else if (driveId) {
      if (folderId === driveId) {
        return `/drives/${driveId}/items/root/children`;
      } else {
        return `/drives/${driveId}/items/${folderId}/children`;
      }
    } else {
      return `/me/drive/items/${folderId}/children`;
    }
  }

  public async createFolder(parent: OneDriveItem, name: string): Promise<OneDriveItem> {
    const res: OneDriveFile = await this.client.api(this.getEndpoint(parent)).post({
      name,
      folder: {},
      // Rename folder if it already exists; the alternative is to throw an error
      '@microsoft.graph.conflictBehavior': 'rename',
    });

    return new OneDriveItem(res, parent.driveId);
  }

  public async renameFolder(folder: OneDriveItem, name: string): Promise<OneDriveItem> {
    try {
      const res: OneDriveFile = await this.client.api(this.getItemPath(folder)).patch({
        name,
        // Fail instead of silently appending a suffix when the name is taken.
        '@microsoft.graph.conflictBehavior': 'fail',
      });

      return new OneDriveItem(res, folder.driveId);
    } catch (err) {
      if (this.isNameConflictError(err)) {
        throw new FriendlyError('folderNameConflict', err);
      }

      throw err;
    }
  }

  private isNameConflictError(err: unknown): boolean {
    const graphError = err as { statusCode?: number; code?: string };
    return graphError?.statusCode === 409 || graphError?.code === 'nameAlreadyExists';
  }

  public async listFolder(
    item: StorageItem,
    onlyFolders: boolean = false,
  ): Promise<StorageListPage> {
    if (item.id === ONE_DRIVE_ROOT) {
      const items: StorageItem[] = [];
      const personalDrive = await this.client.api('/me/drive').get();

      if (this.teamRoot) {
        items.push(MockItem.fromStorageFolder(this.teamRoot, true, 'Team Root'));
      }

      items.push(
        MockItem.folder(
          ONE_DRIVE_PERSONAL_DRIVE_ID,
          ServiceProvider.Microsoft,
          personalDrive.id,
          'Personal Drive',
        ),
      );

      if (!this.isMicrosoftPersonalFull) {
        items.push(
          MockItem.folder(
            ONE_DRIVE_SHAREPOINT_ID,
            ServiceProvider.Microsoft,
            undefined,
            'SharePoint Drives',
            false,
          ),
        );
      }

      return new MockListPage(items);
    }

    switch (item.id) {
      case ONE_DRIVE_SHAREPOINT_ID:
        try {
          const sites = await this.client.api(`/sites?search=`).get();
          const sharepointDrives: StorageItem[] = (
            await Promise.allSettled<StorageItem>(
              sites.value.map(async (site: Site) => {
                try {
                  const drive: OneDriveFile = await this.client
                    .api(`/sites/${site.id}/drive`)
                    .get();

                  return MockItem.folder(
                    drive.id!,
                    ServiceProvider.Microsoft,
                    drive.id,
                    site.displayName!,
                  );
                } catch (err) {
                  this.logger.error(`Couldn't get Sharepoint Site: ${site.id}`);
                  this.logger.error(err);

                  throw new FriendlyError('sharepoint', err);
                }
              }),
            )
          )
            .filter(
              (result): result is PromiseFulfilledResult<StorageItem> =>
                result.status === 'fulfilled',
            )
            .map((result) => result.value);

          return new MockListPage(sharepointDrives);
        } catch (err) {
          return new MockListPage([]);
        }

      case ONE_DRIVE_SHARED_WITH_ME_ID:
        throw new MethodNotImplementedError();

      case ONE_DRIVE_PERSONAL_DRIVE_ID:
      default:
        let req = this.client.api(this.getEndpoint(item)).orderby('name').top(DEFAULT_PAGE_SIZE);

        if (onlyFolders) {
          req = req.filter('folder ne null');
        }

        const res: PageCollection = await req.get();

        return new OneDriveListPage(this.manager, res, onlyFolders, item.driveId);
    }
  }

  public async moveItems(
    targetFolder: StorageItem,
    items: StorageItem[],
  ): Promise<Record<StorageItemId, boolean>> {
    if (!items || items.length === 0) {
      return {};
    }

    return this.batchRequest(
      items,
      (item) =>
        new Request(this.getItemPath(item), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            parentReference: {
              ...(targetFolder.driveId && { driveId: targetFolder.driveId }),
              id: targetFolder.id,
            },
          }),
        }),
      async (res) => (res.ok ? true : false),
    );
  }

  public async trashItems(items: StorageItem[]): Promise<Record<StorageItemId, boolean>> {
    if (!items || items.length === 0) {
      return {};
    }

    return this.batchRequest(
      items,
      (item) =>
        new Request(this.getItemPath(item), {
          method: 'DELETE',
        }),
      async (res) => (res.ok ? true : false),
    );
  }

  public async getNextFolderPage(
    onlyFolders: boolean,
    options: {
      cursor: string;
      driveId?: string;
    },
  ): Promise<StorageListPage> {
    const res: PageCollection = await this.client.api(options.cursor).get();

    return new OneDriveListPage(this.manager, res, onlyFolders, options.driveId);
  }

  public async setCaption(item: Readonly<StorageItem>, caption?: string): Promise<void> {}
}
